import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Quote() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    country: '',
    cocoaGrade: '',
    quantity: '',
    deliveryLocation: '',
    preferredDeliveryDate: '',
    certificationRequirements: '',
    additionalRequirements: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d469qm2hchb23d4ef6qg', {
        method: 'POST',
        body: new URLSearchParams(formDataToSubmit as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          phone: '',
          country: '',
          cocoaGrade: '',
          quantity: '',
          deliveryLocation: '',
          preferredDeliveryDate: '',
          certificationRequirements: '',
          additionalRequirements: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-24 px-4 text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(34, 197, 94, 0.8), rgba(245, 158, 11, 0.8)), url('https://readdy.ai/api/search-image?query=Professional%20business%20quote%20request%20background%20with%20cocoa%20trading%20documents%20contracts%20and%20pricing%20sheets%2C%20modern%20corporate%20office%20environment%20with%20international%20business%20paperwork%20and%20financial%20documents%2C%20clean%20professional%20setting%20emphasizing%20business%20partnerships%20and%20quote%20preparation&width=1200&height=600&seq=quote_hero_001&orientation=landscape')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto max-w-4xl text-center">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-file-text-line text-3xl text-white"></i>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Request a Quote
          </h1>
          <p className="text-xl md:text-2xl mb-8 leading-relaxed max-w-3xl mx-auto">
            Get a personalized quote for premium Nigerian cocoa beans. Our team will assess your requirements and provide competitive pricing tailored to your specific needs.
          </p>
        </div>
      </section>

      {/* Quote Form Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-green-800 mb-6">
                Tell Us About Your Requirements
              </h2>
              <p className="text-xl text-gray-600">
                Please provide detailed information about your cocoa bean requirements. Our team will review your request and respond within 24 hours with a comprehensive quote.
              </p>
            </div>

            <form id="quote-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
              {/* Company Information */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="Enter your company name"
                  />
                </div>
                
                <div>
                  <label htmlFor="contactPerson" className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Person *
                  </label>
                  <input
                    type="text"
                    id="contactPerson"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="Your full name"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="your.email@company.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label htmlFor="country" className="block text-sm font-semibold text-gray-700 mb-2">
                  Country/Region *
                </label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  placeholder="Enter your country or region"
                />
              </div>

              {/* Product Requirements */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="cocoaGrade" className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred Cocoa Grade *
                  </label>
                  <div className="relative">
                    <select
                      id="cocoaGrade"
                      name="cocoaGrade"
                      value={formData.cocoaGrade}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm appearance-none cursor-pointer"
                    >
                      <option value="">Select grade</option>
                      <option value="Grade I">Grade I (≤7% moisture, ≤100 beans/100g)</option>
                      <option value="Grade II">Grade II (≤8% moisture, ≤110 beans/100g)</option>
                      <option value="Premium">Premium (≤6% moisture, ≤95 beans/100g)</option>
                      <option value="Custom">Custom specifications</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400"></i>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="quantity" className="block text-sm font-semibold text-gray-700 mb-2">
                    Quantity Required (MT) *
                  </label>
                  <input
                    type="number"
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    required
                    min="1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="Enter quantity in metric tons"
                  />
                </div>
              </div>

              {/* Delivery Information */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="deliveryLocation" className="block text-sm font-semibold text-gray-700 mb-2">
                    Delivery Location *
                  </label>
                  <input
                    type="text"
                    id="deliveryLocation"
                    name="deliveryLocation"
                    value={formData.deliveryLocation}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="Port or destination city"
                  />
                </div>
                
                <div>
                  <label htmlFor="preferredDeliveryDate" className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred Delivery Date
                  </label>
                  <input
                    type="date"
                    id="preferredDeliveryDate"
                    name="preferredDeliveryDate"
                    value={formData.preferredDeliveryDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {/* Certification Requirements */}
              <div>
                <label htmlFor="certificationRequirements" className="block text-sm font-semibold text-gray-700 mb-2">
                  Certification Requirements
                </label>
                <div className="relative">
                  <select
                    id="certificationRequirements"
                    name="certificationRequirements"
                    value={formData.certificationRequirements}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm appearance-none cursor-pointer"
                  >
                    <option value="">No specific requirements</option>
                    <option value="Rainforest Alliance">Rainforest Alliance Certified</option>
                    <option value="EUDR Compliant">EUDR Compliant</option>
                    <option value="Organic">Organic Certified</option>
                    <option value="Fair Trade">Fair Trade Certified</option>
                    <option value="Multiple">Multiple certifications required</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <i className="ri-arrow-down-s-line text-gray-400"></i>
                  </div>
                </div>
              </div>

              {/* Additional Requirements */}
              <div>
                <label htmlFor="additionalRequirements" className="block text-sm font-semibold text-gray-700 mb-2">
                  Additional Requirements or Comments
                </label>
                <textarea
                  id="additionalRequirements"
                  name="additionalRequirements"
                  value={formData.additionalRequirements}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-vertical"
                  placeholder="Please specify any additional requirements, packaging preferences, payment terms, or other details that would help us prepare an accurate quote..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
              </div>

              {/* Submit Status */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-green-600 text-xl mr-3"></i>
                    <p className="text-green-800 font-medium">
                      Thank you! Your quote request has been submitted successfully. Our team will review your requirements and respond within 24 hours.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-600 text-xl mr-3"></i>
                    <p className="text-red-800 font-medium">
                      There was an error submitting your request. Please try again or contact us directly.
                    </p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="text-center pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-green-600 to-amber-600 text-white px-12 py-4 rounded-full font-semibold text-lg hover:from-green-700 hover:to-amber-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting Request...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Submit Quote Request
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h3 className="text-3xl font-bold text-green-800 mb-6">
            Need Immediate Assistance?
          </h3>
          <p className="text-lg text-gray-600 mb-8">
            For urgent inquiries or if you prefer to discuss your requirements directly, our team is ready to assist you.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-xl text-green-600"></i>
              </div>
              <h4 className="font-semibold text-green-800 mb-2">Email Us</h4>
              <p className="text-gray-600">frontdesk@olatundeintl.com</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-xl text-amber-600"></i>
              </div>
              <h4 className="font-semibold text-green-800 mb-2">Call Us</h4>
              <p className="text-gray-600">+234 (0) 123 456 7890</p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-xl text-green-600"></i>
              </div>
              <h4 className="font-semibold text-green-800 mb-2">Response Time</h4>
              <p className="text-gray-600">Within 24 hours</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}